import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import './App.css';
import ToastProvider from './components/ToastProvider';
import PwaChrome from './components/PwaChrome';
import VeltProviders from './components/VeltProviders';
import AdminPortal from './components/AdminPortal.jsx';
import {
  getHostname,
  getTenantSlug,
  isAdminHost,
  isAppHost,
  isLocalDevHost,
  redirectToApp,
} from './utils/appOrigin';
import useTenantBranding from './hooks/useTenantBranding';
import useTheme from './hooks/useTheme';
import { initProductAnalytics, trackPageview } from './lib/productAnalytics';

import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded pages for code splitting
const LandingPage = lazy(() => import('./components/LandingPage'));
const ProbeLandingPage = lazy(() => import('./components/ProbeLandingPage'));
const TestManagerPortal = lazy(() => import('./components/TestManagerPortal'));
const CheckoutSuccess = lazy(() => import('./components/CheckoutSuccess'));
const TermsPage = lazy(() => import('./components/LegalPages').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./components/LegalPages').then(m => ({ default: m.PrivacyPage })));
const DpaPage = lazy(() => import('./components/LegalPages').then(m => ({ default: m.DpaPage })));
const PublicSharedDocument = lazy(() => import('./components/PublicSharedDocument'));
const PublicAnalyticsPage = lazy(() => import('./components/PublicAnalyticsPage'));
const PublicStatusPage = lazy(() => import('./components/PublicStatusPage'));
const AgentPairPage = lazy(() => import('./components/AgentPairPage'));

const Portal = () => (
  <ErrorBoundary>
    <VeltProviders>
      <TestManagerPortal />
    </VeltProviders>
  </ErrorBoundary>
);

/**
 * Wildcard DNS makes every <anything>.testera.io resolve. Only subdomains a
 * superadmin has enabled get a portal; the rest bounce to app.testera.io.
 */
function TenantGate({ children }) {
  const brand = useTenantBranding();
  const unknown = brand === null && getTenantSlug().length > 0;

  useEffect(() => {
    if (unknown && !getHostname().endsWith('.localhost')) {
      window.location.replace(`https://app.testera.io${window.location.pathname}${window.location.search}${window.location.hash}`);
    }
  }, [unknown]);

  if (brand === undefined || unknown) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-slate-400">
        {unknown ? 'This workspace doesn\u2019t exist. Taking you to Testera\u2026' : 'Loading\u2026'}
      </div>
    );
  }
  return children;
}

function LegacyAppPathRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: '/', search: location.search, hash: location.hash }}
      replace
    />
  );
}

function entitySearchFromPath(id, tab, param, locationSearch) {
  const params = new URLSearchParams(locationSearch || '');
  if (id) params.set(param, id);
  params.set('tab', tab);
  const search = params.toString();
  return search ? `?${search}` : '';
}

/** Map /testruns/:id (and siblings) → /?tab=…&runId=… so deep links open the entity. */
function EntityPathRedirect({ tab, param }) {
  const { id } = useParams();
  const location = useLocation();
  const search = entitySearchFromPath(id, tab, param, location.search);
  return (
    <Navigate
      to={{ pathname: '/', search, hash: location.hash }}
      replace
    />
  );
}

/** On marketing host, bounce /testruns/:id → app.testera.io/?tab=testruns&runId=… */
function MarketingEntityBounce({ tab, param }) {
  const { id } = useParams();
  const location = useLocation();
  useEffect(() => {
    redirectToApp('/', {
      search: entitySearchFromPath(id, tab, param, location.search),
      hash: location.hash,
    });
  }, [id, location.search, location.hash, tab, param]);
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600">
      Opening app…
    </div>
  );
}

function MarketingAppRedirect() {
  const location = useLocation();
  useEffect(() => {
    redirectToApp('/', { search: location.search, hash: location.hash });
  }, [location]);
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600">
      Opening app…
    </div>
  );
}

function InviteRedirect() {
  const { token } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('view', 'signup');
  if (token) params.set('invite', token);
  const search = params.toString();
  const onApp = isAppHost();

  useEffect(() => {
    if (!onApp && !isLocalDevHost()) {
      redirectToApp('/', { search, hash: location.hash });
    }
  }, [onApp, search, location.hash]);

  if (onApp || isLocalDevHost()) {
    return (
      <Navigate
        to={{ pathname: onApp ? '/' : '/app', search: search ? `?${search}` : '', hash: location.hash }}
        replace
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600">
      Redirecting to app…
    </div>
  );
}

function SignupRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('view', 'signup');
  const search = params.toString();
  const onApp = isAppHost();

  useEffect(() => {
    if (!onApp && !isLocalDevHost()) {
      redirectToApp('/', { search, hash: location.hash });
    }
  }, [onApp, search, location.hash]);

  if (onApp || isLocalDevHost()) {
    return (
      <Navigate
        to={{ pathname: onApp ? '/' : '/app', search: search ? `?${search}` : '', hash: location.hash }}
        replace
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600">
      Redirecting to app…
    </div>
  );
}

function AppRoutes() {
  const host = getHostname();
  const onAdminHost = isAdminHost(host);
  const onAppHost = isAppHost(host);
  const onLocalDev = isLocalDevHost(host);

  if (onAdminHost) {
    return (
      <>
        <Route path="/" element={<AdminPortal />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </>
    );
  }

  if (onAppHost) {
    const portal = getTenantSlug() ? <TenantGate><Portal /></TenantGate> : <Portal />;
    return (
      <>
        <Route path="/" element={portal} />
        <Route path="/app" element={<LegacyAppPathRedirect />} />
        <Route path="/app/*" element={<LegacyAppPathRedirect />} />
        <Route path="/testruns/:id" element={<EntityPathRedirect tab="testruns" param="runId" />} />
        <Route path="/test-runs/:id" element={<EntityPathRedirect tab="testruns" param="runId" />} />
        <Route path="/projects/:projectId/testruns/:id" element={<EntityPathRedirect tab="testruns" param="runId" />} />
        <Route path="/projects/:projectId/test-runs/:id" element={<EntityPathRedirect tab="testruns" param="runId" />} />
        <Route path="/testplans/:id" element={<EntityPathRedirect tab="testplans" param="planId" />} />
        <Route path="/projects/:id" element={<EntityPathRedirect tab="projects" param="projectId" />} />
        <Route path="/testcases/:id" element={<EntityPathRedirect tab="testcases" param="caseId" />} />
        <Route path="/tasks/:id" element={<EntityPathRedirect tab="tasks" param="taskId" />} />
        <Route path="/documents/:id" element={<EntityPathRedirect tab="documents" param="docId" />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/success" element={<CheckoutSuccess />} />
        <Route path="/invite/:token" element={<InviteRedirect />} />
        <Route path="/signup" element={<SignupRedirect />} />
        <Route path="/admin" element={<AdminPortal />} />
        <Route path="/agent/pair" element={<AgentPairPage />} />
        <Route path="/share/d/:token" element={<PublicSharedDocument />} />
        <Route path="/share/analytics/:token" element={<PublicAnalyticsPage />} />
        <Route path="/status/:token" element={<PublicStatusPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/dpa" element={<DpaPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </>
    );
  }

  // Marketing host (testera.io, www, localhost dev)
  return (
    <>
      <Route path="/" element={<LandingPage />} />
      <Route path="/probe" element={<ProbeLandingPage />} />
      <Route path="/app" element={onLocalDev ? <Portal /> : <MarketingAppRedirect />} />
      <Route path="/app/*" element={onLocalDev ? <Portal /> : <MarketingAppRedirect />} />
      <Route path="/testruns/:id" element={<MarketingEntityBounce tab="testruns" param="runId" />} />
      <Route path="/test-runs/:id" element={<MarketingEntityBounce tab="testruns" param="runId" />} />
      <Route path="/projects/:projectId/testruns/:id" element={<MarketingEntityBounce tab="testruns" param="runId" />} />
      <Route path="/projects/:projectId/test-runs/:id" element={<MarketingEntityBounce tab="testruns" param="runId" />} />
      <Route path="/testplans/:id" element={<MarketingEntityBounce tab="testplans" param="planId" />} />
      <Route path="/projects/:id" element={<MarketingEntityBounce tab="projects" param="projectId" />} />
      <Route path="/testcases/:id" element={<MarketingEntityBounce tab="testcases" param="caseId" />} />
      <Route path="/tasks/:id" element={<MarketingEntityBounce tab="tasks" param="taskId" />} />
      <Route path="/documents/:id" element={<MarketingEntityBounce tab="documents" param="docId" />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/success" element={<CheckoutSuccess />} />
      <Route path="/invite/:token" element={<InviteRedirect />} />
      <Route path="/signup" element={<SignupRedirect />} />
      <Route path="/admin" element={<AdminPortal />} />
      <Route
        path="/agent/pair"
        element={onLocalDev ? <AgentPairPage /> : <MarketingAgentPairBounce />}
      />
      <Route path="/share/d/:token" element={<PublicSharedDocument />} />
        <Route path="/share/analytics/:token" element={<PublicAnalyticsPage />} />
        <Route path="/status/:token" element={<PublicStatusPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/dpa" element={<DpaPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </>
  );
}

function MarketingAgentPairBounce() {
  const location = useLocation();
  useEffect(() => {
    redirectToApp('/agent/pair', { search: location.search, hash: location.hash });
  }, [location.search, location.hash]);
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600">
      Opening app…
    </div>
  );
}

function AnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    initProductAnalytics();
  }, []);
  useEffect(() => {
    trackPageview(`${location.pathname}${location.search || ''}`);
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  // Apply .dark on <html> so --color-surface-* tokens match the dark chrome.
  // Without this, bg-surface-1 stays :root white and light text becomes unreadable.
  useTheme();

  return (
    <ErrorBoundary>
      <ToastProvider>
        <BrowserRouter>
          <AnalyticsRouteTracker />
          <PwaChrome />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-600">Loading…</div>}>
            <Routes>
              {AppRoutes()}
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
